/**
 * studio-preview.js — one paragraph, one voice, heard instantly.
 *
 * Caches synthesised lines in memory (keyed by text + voice + speed) so
 * hopping between voices is instant after the first hit, and guarantees only
 * one preview plays at a time.
 */
import api from "@/lib/api";

const cache = new Map();
let current = null;
const listeners = new Set();

function keyOf(text, voice, speed, prons) {
  return `${voice}|${speed}|${JSON.stringify(prons || [])}|${text}`;
}

export function onPreviewChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(state) {
  listeners.forEach(fn => { try { fn(state); } catch (_) { /* ignore */ } });
}

export function stopPreview() {
  if (current) {
    try { current.pause(); } catch (_) { /* ignore */ }
    current = null;
  }
  emit({ playing: null });
}

/**
 * Synthesise (or reuse) one line and play it.
 * @returns {Promise<void>} resolves once playback has started
 */
export async function previewLine({ text, voice, speed = 1.0, pronunciations = [], id = null }) {
  const clean = (text || "").trim().slice(0, 600);
  if (!clean) throw new Error("empty");
  stopPreview();
  const k = keyOf(clean, voice, speed, pronunciations);
  let b64 = cache.get(k);
  if (!b64) {
    const { data } = await api.post("/studio/preview", {
      text: clean, voice, speed,
      pronunciations: (pronunciations || []).map(p => ({ from_text: p.from_text, to_text: p.to_text })),
    }, { timeout: 60000 });
    b64 = data.audio_b64;
    cache.set(k, b64);
  }
  const el = new Audio(`data:audio/mpeg;base64,${b64}`);
  current = el;
  el.addEventListener("ended", () => { if (current === el) { current = null; emit({ playing: null }); } });
  emit({ playing: id ?? k });
  await el.play();
}
